import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment,
  Grid,
  Typography,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn,
  MyLocation,
  Map,
  Edit,
  Check,
  Clear,
  ExpandMore,
  ExpandLess,
  Search,
  GpsFixed,
  Place
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import locationService from '../../../services/locationService';
import placesService from '../../../services/placesService';
import DialogCloseButton from '../DialogCloseButton';
import MapPinPicker from '../MapPinPicker';

// Styled components
const MapPreview = styled(Paper)(({ theme }) => ({
  height: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[100],
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.grey[200]
  }
}));

const LocationResult = styled(ListItem)(({ theme, selected }) => ({
  cursor: 'pointer',
  border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

// Zimbabwe major cities for fallback suggestions
const ZIMBABWE_CITIES = [
  'Harare', 'Bulawayo', 'Chitungwiza', 'Mutare', 'Gweru', 
  'Kwekwe', 'Kadoma', 'Masvingo', 'Chinhoyi', 'Marondera'
];

const LocationInput = ({ 
  value = null,
  onChange,
  label = "Location",
  placeholder = "Enter project location...",
  required = false,
  error = false,
  helperText = "",
  showMapPreview = true,
  allowCoordinates = true,
  allowManualEntry = true,
  allowGPSLocation = true,
  defaultInputMethod = "manual", // "manual" | "coordinates"
  style = {},
  ...props 
}) => {
  // Ensure default is 'manual' if 'autocomplete' is passed (for backward compatibility)
  const initialMethod = defaultInputMethod === 'autocomplete' ? 'manual' : defaultInputMethod;
  const [inputMethod, setInputMethod] = useState(initialMethod);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(value);
  const [manualAddress, setManualAddress] = useState({
    streetNumber: '',
    street: '',
    suburb: '',
    city: '',
    province: '',
    postalCode: '',
    country: ''
  });
  const [coordinates, setCoordinates] = useState({
    lat: '',
    lng: ''
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [nearestCity, setNearestCity] = useState(null);
  const [pinPickerOpen, setPinPickerOpen] = useState(false);
  const [pinPickerCenter, setPinPickerCenter] = useState(null);
  // The pin picker needs the Google Maps JS API key. Hidden if it is absent so
  // we never show a button that opens a broken map.
  const mapKeyAvailable = !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize PlacesService on mount
  useEffect(() => {
    placesService.initialize().then((initialized) => {
      if (initialized) {
        console.log('LocationInput: Google Places API ready');
      } else {
        console.log('LocationInput: Using fallback location data');
      }
    });
  }, []);

  // Initialize with existing value
  useEffect(() => {
    if (value) {
      setSelectedLocation(value);
      if (value.address) {
        setSearchQuery(value.address);
      }
      if (value.coordinates) {
        setCoordinates({
          lat: value.coordinates.lat?.toString() || '',
          lng: value.coordinates.lng?.toString() || ''
        });
      }
      if (value.street || value.streetNumber) {
        setManualAddress({
          streetNumber: value.streetNumber || '',
          street: value.streetName || value.street || '',
          suburb: value.suburb || '',
          city: value.city || '',
          province: value.province || '',
          postalCode: value.postalCode || '',
          country: value.country || ''
        });
      }
    }
  }, [value]);

  // Debounced search using PlacesService
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    
    try {
      // Use the new PlacesService for address predictions
      const result = await placesService.getAddressPredictions(query.trim());

      if (result.success && result.data && result.data.length > 0) {
        setSuggestions(result.data);
        setShowSuggestions(true);
        console.log('LocationInput: Found', result.data.length, 'suggestions');
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        console.log('LocationInput: No suggestions found');
      }
    } catch (error) {
      console.error('LocationInput: Search error:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle search input
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If query is too short, clear suggestions immediately
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Set new timeout for debounced search (reduced from 300ms to 200ms for better responsiveness)
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 200);
  };

  // Handle location selection
  const handleLocationSelect = async (location) => {
    setSelectedLocation(location);
    setSearchQuery(location.address);
    setShowSuggestions(false);
    
    // If this is a Google Places result, get detailed information
    if (location.placeId && location.type === 'google_place') {
      setIsLoading(true);
      try {
        const detailsResult = await placesService.getPlaceDetails(location.placeId);
        if (detailsResult.success) {
          const detailedLocation = detailsResult.data;
          setCoordinates({
            lat: detailedLocation.coordinates?.lat?.toString() || '',
            lng: detailedLocation.coordinates?.lng?.toString() || ''
          });

          const locationData = {
            type: 'selected',
            address: detailedLocation.address,
            name: detailedLocation.name,
            coordinates: detailedLocation.coordinates,
            addressComponents: detailedLocation.addressComponents,
            placeId: location.placeId,
            source: 'google_places'
          };

          setSelectedLocation(locationData);
          onChange?.(locationData);
        }
      } catch (error) {
        console.error('Error getting place details:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Handle fallback locations
      if (location.coordinates) {
        setCoordinates({
          lat: location.coordinates.lat?.toString() || '',
          lng: location.coordinates.lng?.toString() || ''
        });
      }

      const locationData = {
        type: 'selected',
        address: location.address,
        name: location.name,
        coordinates: location.coordinates,
        source: 'fallback'
      };

      onChange?.(locationData);
    }
  };

  // Handle manual address change
  const handleManualAddressChange = (field, value) => {
    const updatedAddress = { ...manualAddress, [field]: value };
    setManualAddress(updatedAddress);

    // Create formatted address
    const fullStreet = [updatedAddress.streetNumber, updatedAddress.street].filter(Boolean).join(' ');
    const addressParts = [
      fullStreet,
      updatedAddress.suburb,
      updatedAddress.city,
      updatedAddress.province,
      updatedAddress.country
    ].filter(Boolean);

    const formattedAddress = addressParts.join(', ');

    const locationData = {
      type: 'manual',
      address: formattedAddress,
      streetNumber: updatedAddress.streetNumber,
      street: fullStreet,
      streetName: updatedAddress.street,
      suburb: updatedAddress.suburb,
      city: updatedAddress.city,
      province: updatedAddress.province,
      postalCode: updatedAddress.postalCode,
      country: updatedAddress.country,
      coordinates: null,
      source: 'manual'
    };

    setSelectedLocation(locationData);
    onChange?.(locationData);
  };

  // Handle coordinate input
  const handleCoordinatesChange = async (field, value) => {
    const updatedCoords = { ...coordinates, [field]: value };
    setCoordinates(updatedCoords);

    if (updatedCoords.lat && updatedCoords.lng) {
      const lat = parseFloat(updatedCoords.lat);
      const lng = parseFloat(updatedCoords.lng);

      if (!isNaN(lat) && !isNaN(lng)) {
        // Try reverse geocoding first with PlacesService. If it is unavailable,
        // coordinates are still a valid project location.
        try {
          const reverseGeoResult = await placesService.reverseGeocode(lat, lng);
          if (reverseGeoResult.success) {
            const locationData = {
              type: 'coordinates',
              address: reverseGeoResult.data.address,
              coordinates: { lat, lng },
              addressComponents: reverseGeoResult.data.addressComponents,
              source: 'reverse_geocoding'
            };

            setSelectedLocation(locationData);
            onChange?.(locationData);
            return;
          }
        } catch (error) {
          console.log('Reverse geocoding failed, using coordinate fallback');
        }

        // Preserve the Zimbabwe nearest-city helper where it applies, but do not
        // block projects that only have coordinates outside those bounds.
        let cityResult = { data: null };
        try {
          const boundsValidation = await locationService.validateLocationBounds(lat, lng);
          if (boundsValidation.valid) {
            cityResult = await locationService.findNearestCity(lat, lng);
          }
        } catch (error) {
          console.log('Coordinate bounds lookup failed, using raw coordinates');
        }

        setNearestCity(cityResult.data);

        const reverseGeocodedAddress = cityResult.data
          ? `Near ${cityResult.data.name}`
          : `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

        const locationData = {
          type: 'coordinates',
          address: reverseGeocodedAddress,
          coordinates: { lat, lng },
          nearestCity: cityResult.data,
          source: cityResult.data ? 'coordinates' : 'coordinates_only'
        };

        setSelectedLocation(locationData);
        onChange?.(locationData);
      }
    }
  };

  // Get current GPS location
  const handleGetGPSLocation = async () => {
    setIsLoading(true);
    
    try {
      const locationResult = await locationService.getCurrentLocation();
      const { lat, lng } = locationResult.data;
      
      setGpsLocation(locationResult.data);
      setCoordinates({
        lat: lat.toString(),
        lng: lng.toString()
      });

      // Find nearest city
      const cityResult = await locationService.findNearestCity(lat, lng);
      setNearestCity(cityResult.data);

      const gpsAddress = cityResult.data ? 
        `Near ${cityResult.data.name}` : 
        `${lat.toFixed(4)}, ${lng.toFixed(4)}`;

      const locationData = {
        type: 'gps',
        address: gpsAddress,
        coordinates: { lat, lng },
        accuracy: locationResult.data.accuracy,
        nearestCity: cityResult.data,
        source: 'gps'
      };

      setSelectedLocation(locationData);
      setSearchQuery(gpsAddress);
      onChange?.(locationData);
      
    } catch (error) {
      console.error('GPS location error:', error);
      // Handle error appropriately
    } finally {
      setIsLoading(false);
    }
  };

  // Get coordinates for Zimbabwe cities (fallback data)
  const getCoordinatesForCity = (cityName) => {
    const cityCoords = {
      'Harare': { lat: -17.8252, lng: 31.0335 },
      'Bulawayo': { lat: -20.1619, lng: 28.5810 },
      'Chitungwiza': { lat: -18.0086, lng: 31.0614 },
      'Mutare': { lat: -18.9707, lng: 32.6473 },
      'Gweru': { lat: -19.4500, lng: 29.8167 },
      'Kwekwe': { lat: -18.9167, lng: 29.8167 },
      'Kadoma': { lat: -18.3333, lng: 29.9167 },
      'Masvingo': { lat: -20.0638, lng: 30.8276 },
      'Chinhoyi': { lat: -17.3667, lng: 30.2000 },
      'Marondera': { lat: -18.1851, lng: 31.5511 }
    };
    return cityCoords[cityName] || null;
  };

  // Extract a usable {lat,lng} from a location object regardless of shape.
  const getCoordinatePair = (loc) => {
    const c = loc?.coordinates;
    const lat = Number(c?.lat);
    const lng = Number(c?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  };

  // Open the satellite pin picker. If we already have coordinates, the pin starts
  // there. Otherwise we try to centre the map on whatever the user has typed
  // (geocode the address, then fall back to a known Zimbabwe city, then nothing —
  // the picker defaults to a country-level view).
  const openPinPicker = async () => {
    const existing = getCoordinatePair(selectedLocation);
    if (existing) {
      setPinPickerCenter(null);
      setPinPickerOpen(true);
      return;
    }

    let center = null;
    const addressText =
      selectedLocation?.address ||
      searchQuery ||
      [manualAddress.suburb, manualAddress.city].filter(Boolean).join(', ');

    if (addressText) {
      try {
        const geo = await placesService.geocodeAddress(addressText);
        if (geo?.success && geo.data?.coordinates) {
          center = geo.data.coordinates;
        }
      } catch (_) {
        // Geocoding unavailable — fall through to city/country defaults.
      }
    }
    if (!center && manualAddress.city) {
      center = getCoordinatesForCity(manualAddress.city);
    }

    setPinPickerCenter(center);
    setPinPickerOpen(true);
  };

  // The user dropped a pin — coordinates are now authoritative. Keep any address
  // text we already have and mark the source so downstream knows it was pinned.
  const handlePinConfirm = ({ lat, lng }) => {
    setPinPickerOpen(false);
    setCoordinates({ lat: lat.toString(), lng: lng.toString() });

    const baseAddress =
      selectedLocation?.address ||
      searchQuery ||
      `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

    const locationData = {
      ...(selectedLocation || {}),
      type: selectedLocation?.type || 'map_pin',
      address: baseAddress,
      coordinates: { lat, lng },
      source: 'map_pin',
    };

    setSelectedLocation(locationData);
    onChange?.(locationData);
  };

  // Render input method selector
  const renderInputMethodSelector = () => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Input Method
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {allowManualEntry && (
          <Chip
            label="Manual Entry"
            icon={<Edit />}
            onClick={() => setInputMethod('manual')}
            color={inputMethod === 'manual' ? 'primary' : 'default'}
            variant={inputMethod === 'manual' ? 'filled' : 'outlined'}
          />
        )}
        {allowCoordinates && (
          <Chip
            label="Coordinates"
            icon={<Place />}
            onClick={() => setInputMethod('coordinates')}
            color={inputMethod === 'coordinates' ? 'primary' : 'default'}
            variant={inputMethod === 'coordinates' ? 'filled' : 'outlined'}
          />
        )}
      </Box>
    </Box>
  );

  // Render autocomplete input
  const renderAutocompleteInput = () => (
    <Box sx={{ position: 'relative' }}>
      <TextField
        ref={inputRef}
        fullWidth
        label={label}
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        required={required}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LocationOn color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isLoading && <CircularProgress size={20} />}
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchQuery('');
                    setShowSuggestions(false);
                    setSelectedLocation(null);
                    onChange?.(null);
                  }}
                >
                  <Clear />
                </IconButton>
              )}
            </InputAdornment>
          )
        }}
        {...props}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: 200,
            overflow: 'auto',
            mt: 1
          }}
        >
          <List dense>
            {suggestions.map((suggestion) => (
              <LocationResult
                key={suggestion.id}
                onClick={() => handleLocationSelect(suggestion)}
                selected={selectedLocation?.id === suggestion.id}
              >
                <ListItemText
                  primary={suggestion.name}
                  secondary={suggestion.address}
                />
                <Chip
                  label={suggestion.type}
                  size="small"
                  variant="outlined"
                />
              </LocationResult>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );

  // Handle selecting a suggestion — fetch details and auto-fill all fields
  const handleSuggestionSelect = async (suggestion) => {
    if (!suggestion) return;

    // If it's a Google Places result, fetch full details to populate all fields
    if (suggestion.placeId && suggestion.type === 'google_place') {
      setIsLoading(true);
      try {
        const detailsResult = await placesService.getPlaceDetails(suggestion.placeId);
        if (detailsResult.success && detailsResult.data) {
          const d = detailsResult.data;
          const ac = d.addressComponents || {};

          // Google frequently omits `locality` for Zimbabwe addresses, returning
          // the place under sublocality or administrative_area_level_2 instead.
          // Fall back through those so the City field actually populates.
          const resolvedCity =
            ac.locality ||
            ac.administrativeAreaLevel2 ||
            ac.sublocality ||
            ac.administrativeAreaLevel1 ||
            '';
          const updatedAddress = {
            streetNumber: ac.streetNumber || '',
            street: ac.route || suggestion.name || '',
            suburb: ac.sublocality || '',
            city: resolvedCity,
            province: ac.administrativeAreaLevel1 || '',
            postalCode: ac.postalCode || '',
            country: ac.country || '',
          };
          setManualAddress(updatedAddress);
          setSearchQuery(d.address || suggestion.address);

          const coords = d.coordinates || null;
          if (coords) {
            setCoordinates({ lat: coords.lat?.toString() || '', lng: coords.lng?.toString() || '' });
          }

          const locationData = {
            type: 'selected',
            address: d.address || suggestion.address,
            name: d.name || suggestion.name,
            coordinates: coords,
            streetNumber: updatedAddress.streetNumber,
            street: [updatedAddress.streetNumber, updatedAddress.street].filter(Boolean).join(' '),
            streetName: updatedAddress.street,
            suburb: updatedAddress.suburb,
            city: updatedAddress.city,
            province: updatedAddress.province,
            postalCode: updatedAddress.postalCode,
            country: updatedAddress.country,
            addressComponents: ac,
            placeId: suggestion.placeId,
            source: 'google_places',
          };
          setSelectedLocation(locationData);
          onChange?.(locationData);
          return;
        }
      } catch (err) {
        console.error('Error fetching place details:', err);
      } finally {
        setIsLoading(false);
      }
    }

    // Fallback for non-Google results
    const updatedAddress = {
      ...manualAddress,
      street: suggestion.name || manualAddress.street,
      city: suggestion.city || manualAddress.city,
      province: suggestion.province || manualAddress.province,
      country: suggestion.country || manualAddress.country,
    };
    setManualAddress(updatedAddress);

    const locationData = {
      type: 'selected',
      address: suggestion.address,
      name: suggestion.name,
      coordinates: suggestion.coordinates || null,
      ...updatedAddress,
      source: 'fallback',
    };
    setSelectedLocation(locationData);
    onChange?.(locationData);
  };

  // Render manual address form — type-ahead search that auto-fills all fields
  const renderManualAddressForm = () => (
    <Grid container spacing={2}>
      {/* Primary search field — autocomplete suggestions populate all fields below */}
      <Grid item xs={12}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            fullWidth
            label="Search Address"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Start typing an address..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {isLoading && <CircularProgress size={20} />}
                  {searchQuery && (
                    <IconButton size="small" onClick={() => {
                      setSearchQuery('');
                      setShowSuggestions(false);
                      setSuggestions([]);
                    }}>
                      <Clear />
                    </IconButton>
                  )}
                </InputAdornment>
              )
            }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, maxHeight: 250, overflow: 'auto', mt: 0.5 }}>
              <List dense>
                {suggestions.map((s) => (
                  <LocationResult key={s.id} onClick={() => { handleSuggestionSelect(s); setShowSuggestions(false); }}>
                    <ListItemText primary={s.name} secondary={s.address} />
                    {s.type === 'google_place' && <Chip label="Google" size="small" color="primary" variant="outlined" />}
                  </LocationResult>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Grid>

      {/* Auto-filled fields — editable after selection */}
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          label="Street Number"
          value={manualAddress.streetNumber}
          onChange={(e) => handleManualAddressChange('streetNumber', e.target.value)}
          placeholder="e.g., 2453"
        />
      </Grid>
      <Grid item xs={12} sm={9}>
        <TextField
          fullWidth
          label="Street Address"
          value={manualAddress.street}
          onChange={(e) => handleManualAddressChange('street', e.target.value)}
          placeholder="e.g., Vuka Crescent"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Suburb/Area"
          value={manualAddress.suburb}
          onChange={(e) => handleManualAddressChange('suburb', e.target.value)}
          placeholder="e.g., Borrowdale"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="City"
          value={manualAddress.city}
          onChange={(e) => handleManualAddressChange('city', e.target.value)}
          placeholder="e.g., Harare"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="State/Province/Region"
          value={manualAddress.province}
          onChange={(e) => handleManualAddressChange('province', e.target.value)}
          placeholder="e.g., Harare Province"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Postal/ZIP Code"
          value={manualAddress.postalCode}
          onChange={(e) => handleManualAddressChange('postalCode', e.target.value)}
          placeholder="e.g., 00263"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Country"
          value={manualAddress.country}
          onChange={(e) => handleManualAddressChange('country', e.target.value)}
          placeholder="e.g., Zimbabwe"
        />
      </Grid>
    </Grid>
  );

  // Render coordinates input
  const renderCoordinatesInput = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Latitude"
          type="number"
          value={coordinates.lat}
          onChange={(e) => handleCoordinatesChange('lat', e.target.value)}
          placeholder="-17.8252"
          inputProps={{ step: 'any' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <GpsFixed color="action" />
              </InputAdornment>
            )
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Longitude"
          type="number"
          value={coordinates.lng}
          onChange={(e) => handleCoordinatesChange('lng', e.target.value)}
          placeholder="31.0335"
          inputProps={{ step: 'any' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <GpsFixed color="action" />
              </InputAdornment>
            )
          }}
        />
      </Grid>
      {nearestCity && (
        <Grid item xs={12}>
          <Alert severity="info">
            Nearest city: {nearestCity.name} ({nearestCity.distance}km away)
          </Alert>
        </Grid>
      )}
    </Grid>
  );

  // Render map preview
  const renderMapPreview = () => {
    if (!showMapPreview || !selectedLocation?.coordinates) return null;

    return (
      <MapPreview onClick={() => setMapDialogOpen(true)} sx={{ mt: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Map sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Click to view location on map
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedLocation.coordinates.lat.toFixed(4)}, {selectedLocation.coordinates.lng.toFixed(4)}
          </Typography>
        </Box>
      </MapPreview>
    );
  };

  return (
    <Box sx={style}>
      {renderInputMethodSelector()}
      
      {inputMethod === 'manual' && renderManualAddressForm()}
      {inputMethod === 'coordinates' && renderCoordinatesInput()}
      
      {selectedLocation && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Check />
              <Typography variant="body2">
                Location selected: {selectedLocation.address}
              </Typography>
            </Box>
            {selectedLocation.source && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Source: {selectedLocation.source}
                {selectedLocation.accuracy && ` (±${Math.round(selectedLocation.accuracy)}m)`}
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      {/* Pin-on-map entry point — available regardless of input method, so a
          property Google cannot resolve from its address can still be located
          precisely on the satellite map. */}
      {mapKeyAvailable && (
        <Box sx={{ mt: 2 }}>
          <Box
            onClick={openPinPicker}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.25,
              borderRadius: 1,
              border: theme => `1px dashed ${theme.palette.primary.main}`,
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Place fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {getCoordinatePair(selectedLocation)
                ? 'Adjust exact location on the map'
                : "Address not found? Pin it on the map"}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Best for new stands, informal areas, or unnumbered plots.
          </Typography>
        </Box>
      )}

      <MapPinPicker
        open={pinPickerOpen}
        onClose={() => setPinPickerOpen(false)}
        onConfirm={handlePinConfirm}
        initialPin={getCoordinatePair(selectedLocation)}
        initialCenter={pinPickerCenter}
        addressLabel={selectedLocation?.address || searchQuery || ''}
      />

      {renderMapPreview()}

      {/* Map Dialog — Google Maps embed when coordinates are available */}
      <Dialog
        open={mapDialogOpen}
        onClose={() => setMapDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setMapDialogOpen(false)} />
        <DialogTitle>Location Preview</DialogTitle>
        <DialogContent>
          {selectedLocation?.coordinates && process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
            <Box sx={{ height: 450, width: '100%' }}>
              <iframe
                title="Location Map"
                width="100%"
                height="100%"
                style={{ border: 0, borderRadius: 8 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${selectedLocation.coordinates.lat},${selectedLocation.coordinates.lng}&zoom=15`}
              />
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedLocation.address}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedLocation.coordinates.lat.toFixed(6)}, {selectedLocation.coordinates.lng.toFixed(6)}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 1 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Map sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {selectedLocation?.address || 'No location selected'}
                </Typography>
                {selectedLocation?.coordinates && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {selectedLocation.coordinates.lat.toFixed(6)}, {selectedLocation.coordinates.lng.toFixed(6)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LocationInput;
